/**
 * Zendesk Contact Form Integration
 * Handles department-based routing and Zendesk ticket creation
 */
class ZendeskContactHandler {
  constructor() {
    // Configuration will be loaded from Shopify app settings
    this.zendeskConfig = null;
    this.loadConfiguration();
  }

  // Load configuration from Shopify theme settings
  async loadConfiguration() {
    try {
      // These will be available as global variables injected by Liquid
      this.zendeskConfig = {
        enabled: window.zendeskSettings?.enabled || false,
        subdomain: window.zendeskSettings?.subdomain || '',
        email: window.zendeskSettings?.email || '',
        apiToken: window.zendeskSettings?.apiToken || ''
      };
    } catch (error) {
      console.error('ZendeskContactHandler: Error loading configuration:', error);
      this.zendeskConfig = { enabled: false };
    }
  }

  // Initialize the contact form with Zendesk integration
  async init() {
    if (!this.zendeskConfig) {
      await this.loadConfiguration();
    }
    
    // Find the contact form
    const contactForm = document.querySelector('form[action*="contact"]');
    if (!contactForm) {
      console.warn('ZendeskContactHandler: Contact form not found');
      return;
    }

    // Add event listener for form submission
    contactForm.addEventListener('submit', (event) => this.handleFormSubmit(event));
  }

  // Handle contact form submission
  async handleFormSubmit(event) {
    const form = event.target;
    const formData = new FormData(form);
    
    // Extract contact data
    const contactData = this.extractContactData(formData);
    
    // Detect department
    const department = this.detectDepartment(contactData);
    
    // Only create Zendesk ticket if configured
    if (this.isZendeskConfigured()) {
      try {
        await this.createZendeskTicket(contactData, department, formData);
      } catch (error) {
        console.error('ZendeskContactHandler: Failed to create Zendesk ticket:', error);
      }
    }
  }

  // Extract contact data from form
  extractContactData(formData) {
    const data = {};
    
    for (const [key, value] of formData.entries()) {
      // Clean up Shopify form field names
      const cleanKey = key.replace('contact[', '').replace(']', '');
      data[cleanKey] = value;
    }
    
    // Handle different possible field names for phone
    const phoneValue = data.Phone || data.phone || data['Phone Number'] || data['phone_number'] || 
                      data.telephone || data.Telephone || data['Phone'] || data['phone'] ||
                      Object.values(data).find(val => 
                        typeof val === 'string' && /^\+?[\d\s\-\(\)]+$/.test(val.trim()) && val.trim().length > 6
                      ) || '';
    
    const messageValue = data.comment || data.Comment || data.message || data.Message || 
                        data.body || data.Body || data['Your message'] || data['Message'] ||
                        Object.values(data).find(val => 
                          typeof val === 'string' && val.length > 20 && !val.includes('@')
                        ) || '';
    
    const extractedData = {
      name: data.Name || data.name || data['Name'] || data['name'] || '',
      
      // Contact information
      email: data.email || data.Email || '',
      telephone: phoneValue,
      phone: phoneValue,
      
      // Contact preferences and topic
      preferred_contact: data['preferred_contact'] || data['Preferred Contact Method'] || data['contact_method'] || '',
      topic: data.topic || data['Select a topic'] || data.department || data['Custom field 1'] || '',
      department: data.topic || data['Select a topic'] || data.department || data['Custom field 1'] || '',
      // Message content
      message: messageValue,
      subject: data.subject || data.Subject || '',
      
      // Include all original data for completeness
      ...data
    };
    
   return extractedData;
  }

  // Get department from form data
  detectDepartment(contactData) {
    // Return the selected department or default to Sales
    return contactData.department || 'Sales/Product Order';
  }

  // Create Zendesk ticket via API
  async createZendeskTicket(contactData, department, formData) {
    if (!this.isZendeskConfigured()) {
      return;
    }

    // Extract raw form data for message formatting
    const data = {};
    for (const [key, value] of formData.entries()) {
      const cleanKey = key.replace('contact[', '').replace(']', '');
      data[cleanKey] = value;
    }

    const payload = {
      subject: `Customer Care Request: ${department}`,
      comment: {
        body: this.formatZendeskMessage(contactData, department, data)
      },
      requester: {
        locale_id: 1,
        name: contactData.name || null,
        email: contactData.email || null
      }
    };

    return await this.sendViaZendeskAPI({ ticket: payload });
  }

  // Send ticket via direct Zendesk API
  async sendViaZendeskAPI(ticketData) {
    const auth = btoa(`${this.zendeskConfig.email}:${this.zendeskConfig.apiToken}`);
    const url = `https://${this.zendeskConfig.subdomain}.zendesk.com/api/v2/tickets`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${auth}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(ticketData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ZendeskContactHandler: API Error:', response.status, errorText);
      throw new Error(`Zendesk API request failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  // Format message for Zendesk ticket
  formatZendeskMessage(contactData, department, rawFormData) {
    let message = `Reason For Contacting: ${contactData.topic || department}\n\n`;
    
    // Always present fields (leave blank if missing)
    message += `Name: ${contactData.name || ''}\n`;
    message += `Telephone: ${contactData.telephone || contactData.phone || ''}\n`;
    message += `Email Address: ${contactData.email || ''}\n`;
    message += `Preferred Contact Method: ${contactData.preferred_contact || ''}\n\n`;
    
    // Customer message
    if (contactData.message) {
      message += `Message:\n${contactData.message}`;
    }

    return message.trim();
  }

  // Check if Zendesk is properly configured
  isZendeskConfigured() {
    const isConfigured = !!(this.zendeskConfig && 
                           this.zendeskConfig.enabled && 
                           this.zendeskConfig.subdomain && 
                           this.zendeskConfig.apiToken);
    
    return isConfigured;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  const zendeskHandler = new ZendeskContactHandler();
  await zendeskHandler.init();
});

// Export for manual initialization if needed
window.ZendeskContactHandler = ZendeskContactHandler;
